import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, RefreshCw, AlertCircle, CheckCircle, Smartphone } from 'lucide-react';

interface ServiceWorkerLogEntry {
  timestamp: string;
  message: string;
  level: 'info' | 'error' | 'warn';
}

export const ServiceWorkerDebug = () => {
  const [swStatus, setSwStatus] = useState<string>('checking');
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [logs, setLogs] = useState<ServiceWorkerLogEntry[]>([]);
  const [isListening, setIsListening] = useState(false);

  const checkServiceWorkerStatus = async () => {
    if (!('serviceWorker' in navigator)) {
      setSwStatus('unsupported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        setSwRegistration(registration);
        if (registration.active) {
          setSwStatus('active');
        } else if (registration.installing) {
          setSwStatus('installing');
        } else if (registration.waiting) {
          setSwStatus('waiting');
        } else {
          setSwStatus('registered');
        }
      } else {
        setSwStatus('not-registered');
      }
    } catch (error) {
      console.error('Service worker status check failed:', error);
      setSwStatus('error');
    }
  };

  const startListening = () => {
    if (!isListening) {
      setIsListening(true);
      
      // Listen for service worker log messages
      const handleServiceWorkerMessage = (event) => {
        console.log('Debug: Received SW message:', event.data);
        if (event.data && event.data.type === 'SERVICE_WORKER_LOG') {
          setLogs(prev => [...prev, {
            timestamp: event.data.timestamp,
            message: event.data.message,
            level: event.data.level || 'info'
          }].slice(-50)); // Keep last 50 logs
        }
      };

      // Add multiple listeners to catch all messages
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.addEventListener('message', handleServiceWorkerMessage);
      }
      
      // Also listen for service worker state changes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: '🔔 Service worker controller changed',
          level: 'info' as const
        }].slice(-50));
      });
      
      // Override console methods to capture main thread logs
      const originalLog = console.log;
      const originalError = console.error;
      const originalWarn = console.warn;

      console.log = (...args) => {
        originalLog(...args);
        const message = args.join(' ');
        if (message.includes('Service Worker') || message.includes('🔔')) {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message,
            level: 'info' as const
          }].slice(-50)); // Keep last 50 logs
        }
      };

      console.error = (...args) => {
        originalError(...args);
        const message = args.join(' ');
        if (message.includes('Service Worker') || message.includes('🔔')) {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message,
            level: 'error' as const
          }].slice(-50));
        }
      };

      console.warn = (...args) => {
        originalWarn(...args);
        const message = args.join(' ');
        if (message.includes('Service Worker') || message.includes('🔔')) {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message,
            level: 'warn' as const
          }].slice(-50));
        }
      };

      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: '🔔 Debug logging started - listening for service worker messages...',
        level: 'info' as const
      }]);
    }
  };

  const sendTestNotification = async () => {
    try {
      // Send a test notification to see if service worker receives it
      if (swRegistration && swRegistration.active) {
        // Add debug logging to see if messages work
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: '🔔 Sending test message to service worker...',
          level: 'info' as const
        }]);

        // Post a message to the service worker to simulate a push event
        swRegistration.active.postMessage({
          type: 'TEST_PUSH',
          data: {
            title: 'Test Notification',
            body: 'This is a test from the debug panel',
            icon: '/pwa-icons/icon-192x192.png',
            url: '/'
          }
        });
        
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: '🔔 Test message sent to service worker',
          level: 'info' as const
        }]);

        // Also test if we can trigger a fake push event for debugging
        setTimeout(() => {
          if (swRegistration?.active) {
            // Try to manually trigger push event for testing
            const mockPushEvent = new Event('push');
            setLogs(prev => [...prev, {
              timestamp: new Date().toLocaleTimeString(),
              message: '🔔 Attempting to trigger manual push event for testing',
              level: 'info' as const
            }]);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to send test notification:', error);
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `🔔 Error sending test: ${error.message}`,
        level: 'error' as const
      }]);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  useEffect(() => {
    checkServiceWorkerStatus();
    
    // Check status periodically
    const interval = setInterval(checkServiceWorkerStatus, 5000);
    
    // Also set up message listener immediately
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SERVICE_WORKER_LOG') {
        setLogs(prev => [...prev, {
          timestamp: event.data.timestamp,
          message: event.data.message,
          level: event.data.level || 'info'
        }].slice(-50));
      }
    };

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }
    
    return () => {
      clearInterval(interval);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  const getStatusBadge = () => {
    switch (swStatus) {
      case 'active':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'installing':
        return <Badge variant="secondary"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Installing</Badge>;
      case 'waiting':
        return <Badge variant="secondary">Waiting</Badge>;
      case 'registered':
        return <Badge variant="secondary">Registered</Badge>;
      case 'not-registered':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Not Registered</Badge>;
      case 'unsupported':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Unsupported</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="outline">Checking...</Badge>;
    }
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Service Worker Debug
        </CardTitle>
        <CardDescription>
          Debug service worker status and push notification handling
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              {getStatusBadge()}
            </div>
            {isIOS && (
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                <span className="text-xs text-muted-foreground">
                  iOS Device - PWA Mode: {isStandalone ? 'Yes ✓' : 'No ✗'}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={checkServiceWorkerStatus}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            {swStatus === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestNotification}
              >
                Test
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Push Event Logs</h4>
            <div className="flex gap-2">
              {!isListening && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startListening}
                >
                  Start Logging
                </Button>
              )}
              {logs.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearLogs}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          <ScrollArea className="h-64 w-full border rounded-md p-3">
            {logs.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                {isListening ? 'Waiting for push notifications...' : 'Click "Start Logging" to monitor push events'}
              </div>
            ) : (
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className={`text-xs p-2 rounded ${
                      log.level === 'error'
                        ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200'
                        : log.level === 'warn'
                        ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200'
                        : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    <span className="text-muted-foreground">[{log.timestamp}]</span>{' '}
                    {log.message}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {swRegistration && (
          <div className="text-xs text-muted-foreground space-y-1">
            <div>Scope: {swRegistration.scope}</div>
            <div>Update Via Cache: {(swRegistration as any).updateViaCache || 'default'}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};